/**
 * Canvas text helpers for the map renderer (WI #5531).
 *
 * `TextMeasurer` owns a persistent `measureText` width cache keyed by
 * `font + text` (banner room and label truncation measure the same strings every
 * frame, so caching matters). `truncate` clips a string to a pixel width with an
 * ellipsis using the context's *current* font — callers set `ctx.font` first.
 */

/** Ellipsis-truncate `text` to `maxWidth` using the context's current font. */
export function truncate(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): { text: string; width: number } {
  const full = ctx.measureText(text).width;
  if (full <= maxWidth) {
    return { text, width: full };
  }
  const ellipsis = '…';
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    const candidate = text.slice(0, mid) + ellipsis;
    if (ctx.measureText(candidate).width <= maxWidth) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  const out = text.slice(0, lo) + ellipsis;

  return { text: out, width: ctx.measureText(out).width };
}

/**
 * A run of breaking whitespace: everything JS `\s` matches except the no-break
 * spaces (U+00A0, U+2007, U+202F, U+FEFF), which stay part of their word.
 */
const BREAKING_SPACES =
  /[\t\n\v\f\r \u1680\u2000-\u2006\u2008-\u200a\u2028\u2029\u205f\u3000]+/g;

/** A line may break after these (they stay on the line)… */
const BREAK_AFTER = new Set(['-', '/', '\u2013', '\u2014']);
/**
 * …unless the next character is a digit or whitespace (`1-3`, `2025/2026`), or a
 * combining mark or ZWJ that belongs to the dash's grapheme.
 */
const NO_BREAK_BEFORE = /^[\p{Nd}\p{M}\s\u{200d}]/u;

const ELLIPSIS = '…';

/**
 * Spans up to this many UTF-16 units are measured whole; longer ones on
 * prefixes that double from it, so a long run is measured and split only about
 * as far as one line holds.
 */
const PROBE_UNITS = 32;

/**
 * Grapheme clusters, so a mid-word split never cuts an emoji or an accented
 * letter in half. Code points where `Intl.Segmenter` is missing.
 */
const graphemeSegmenter =
  typeof Intl.Segmenter === 'function'
    ? new Intl.Segmenter(undefined, { granularity: 'grapheme' })
    : null;

/** A place a line may end. */
interface LineBreak {
  /** End of the line's text when it breaks here. */
  end: number;
  /** Start of the next line: past the space that a break at a space consumes. */
  next: number;
}

/**
 * Wrap `text` into lines that each fit within `maxWidth`, up to `maxLines`
 * (WI #5729, browser-like): runs of whitespace collapse to one space and break
 * there; a line also breaks after `-` `/` `–` `—` unless a digit or whitespace
 * follows; a run that does not fit on a line of its own is split between
 * graphemes, without an inserted hyphen. If the text needs more lines, the last
 * one holds as much of the rest as fits, followed by `…`. Each line costs about
 * what it holds, however long the text (this runs on every frame).
 * Caller must set `ctx.font` before calling.
 */
export function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number,
): string[] {
  const norm = text.replace(BREAKING_SPACES, ' ').replace(/^ | $/g, '');
  const breaks = lineBreaks(norm);
  const fits = (from: number, to: number): boolean =>
    ctx.measureText(norm.slice(from, to)).width <= maxWidth;
  // The end of a prefix of [from, to) that does not fit, or -1 if all of it
  // fits — probed on doubling prefixes (PROBE_UNITS).
  const overflowEnd = (from: number, to: number): number => {
    for (let len = PROBE_UNITS; ; len *= 2) {
      const end = from + len >= to ? to : codePointBoundary(norm, from + len);
      if (!fits(from, end)) {
        return end;
      }
      if (end === to) {
        return -1;
      }
    }
  };
  const lines: string[] = [];
  let start = 0;
  let b = 0;

  while (start < norm.length) {
    while (breaks[b].end <= start) {
      b++;
    }
    // Greedy: the last break whose line still fits. `overflow` ends a piece
    // that does not — the last visible line never reaches past it.
    let line: LineBreak | null = null;
    let overflow = norm.length;
    for (let i = b; i < breaks.length; i++) {
      const end = overflowEnd(start, breaks[i].end);
      if (end >= 0) {
        overflow = end;
        break;
      }
      line = breaks[i];
    }
    if (!line) {
      // The run up to the next break does not fit on a line of its own: split
      // it at the last grapheme that fits — at least one, so the loop advances.
      // `overflow` ends a piece of it that is already too wide.
      const run = breaks[b];
      const bounds = graphemeBounds(norm.slice(start, overflow));
      let lo = 1;
      let hi = Math.max(1, bounds.length - 2);
      while (lo < hi) {
        const mid = Math.ceil((lo + hi) / 2);
        if (fits(start, start + bounds[mid])) {
          lo = mid;
        } else {
          hi = mid - 1;
        }
      }
      let cut = start + bounds[lo];
      if (bounds.length === 2 && cut < run.end) {
        // One grapheme longer than the probed piece: take it whole.
        cut = start + graphemeBounds(norm.slice(start, run.end))[1];
      }
      line = cut === run.end ? run : { end: cut, next: cut };
    }

    if (lines.length >= maxLines - 1 && line.next < norm.length) {
      lines.push(ellipsize(ctx, norm.slice(start, overflow), maxWidth));
      break;
    }
    lines.push(norm.slice(start, line.end));
    start = line.next;
  }

  return lines;
}

/** Where `text` (whitespace already collapsed) may break, in order; the last is its end. */
function lineBreaks(text: string): LineBreak[] {
  const breaks: LineBreak[] = [];
  for (let i = 0; i < text.length; i++) {
    if (text[i] === ' ') {
      breaks.push({ end: i, next: i + 1 });
    } else if (
      BREAK_AFTER.has(text[i]) &&
      i + 1 < text.length &&
      !NO_BREAK_BEFORE.test(text.slice(i + 1, i + 3))
    ) {
      breaks.push({ end: i + 1, next: i + 1 });
    }
  }
  breaks.push({ end: text.length, next: text.length });

  return breaks;
}

/** `i`, or one further when it falls between the halves of a surrogate pair. */
function codePointBoundary(text: string, i: number): number {
  const unit = text.charCodeAt(i - 1);

  return unit >= 0xd800 && unit <= 0xdbff ? i + 1 : i;
}

/** Grapheme boundaries of `text`, from 0 to `text.length`. */
function graphemeBounds(text: string): number[] {
  const bounds = [0];
  if (graphemeSegmenter) {
    for (const { index, segment } of graphemeSegmenter.segment(text)) {
      bounds.push(index + segment.length);
    }
  } else {
    for (const codePoint of text) {
      bounds.push(bounds[bounds.length - 1] + codePoint.length);
    }
  }

  return bounds;
}

/**
 * The longest grapheme prefix of `text` that fits within `maxWidth` together with
 * `…`; whitespace right before the `…` (a no-break space too) is dropped. Not
 * `truncate()`, which slices UTF-16 code units.
 */
function ellipsize(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string {
  const bounds = graphemeBounds(text);
  const withEllipsis = (k: number): string =>
    text.slice(0, bounds[k]).replace(/\s+$/, '') + ELLIPSIS;
  let lo = 0;
  let hi = bounds.length - 1;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (ctx.measureText(withEllipsis(mid)).width <= maxWidth) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }

  return withEllipsis(lo);
}

/** Cached text measurement, keyed by `font + text`. */
export class TextMeasurer {
  private readonly cache = new Map<string, number>();

  /** Measure `text` in `font`, caching the result. Sets `ctx.font` as a side effect. */
  measure(ctx: CanvasRenderingContext2D, text: string, font: string): number {
    const key = `${font} ${text}`;
    let width = this.cache.get(key);
    if (width === undefined) {
      ctx.font = font;
      width = ctx.measureText(text).width;
      this.cache.set(key, width);
    }

    return width;
  }

  /** Ellipsis-truncate using the context's current font (convenience re-export). */
  truncate(
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
  ): { text: string; width: number } {
    return truncate(ctx, text, maxWidth);
  }
}
